/**
 * PATH: packages/ui/src/components/settings/entities-tab/EntityCard.tsx
 * PURPOSE: Render a single entity row with Xero tenant linking controls
 * WHY: Extracted from EntitiesTab.tsx to keep files focused and under 300 lines
 *
 * FLOW:
 *   ┌──────────┐    ┌──────────────┐    ┌───────────────┐
 *   │  Entity  │───▶│ Display Card │───▶│ Link / Unlink │
 *   └──────────┘    └──────────────┘    └───────────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: icons
 *   - ./entities-tab-types: shared interfaces
 */

import {
  Building2,
  Link2,
  Unlink,
  Star,
  Trash2,
} from 'lucide-react'
import type { OrganisationEntity, XeroTenant } from './entities-tab-types'

export interface EntityCardProps {
  entity: OrganisationEntity
  tenants: XeroTenant[]
  selectedTenantId: string
  saving: boolean
  /**
   * Whether Xero is applicable to this org. Defaults to true. When false
   * the Xero link/unlink row is hidden — the card still shows the entity
   * identity + Set Default / Edit / Delete controls.
   */
  xeroApplicable?: boolean
  onSelectTenant: (tenantId: string) => void
  onSetDefault: () => void
  onEdit: () => void
  onDelete: () => void
  onLink: () => void
  onUnlink: () => void
}

export function EntityCard({
  entity,
  tenants,
  selectedTenantId,
  saving,
  xeroApplicable = true,
  onSelectTenant,
  onSetDefault,
  onEdit,
  onDelete,
  onLink,
  onUnlink,
}: EntityCardProps) {
  return (
    <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: entity.color || '#6366f1' }}
          >
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-slate-900 dark:text-white">{entity.name}</h4>
              {entity.is_default && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                  <Star className="w-3 h-3" /> Default
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {entity.legal_name || 'Legal name not set'} • {entity.code || 'No code'}
            </p>
            <p className="text-xs text-slate-400">
              Jobs: {entity.job_count ?? 0}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!entity.is_default && (
            <button
              onClick={onSetDefault}
              disabled={saving}
              className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              Set Default
            </button>
          )}
          <button
            onClick={onEdit}
            className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Edit
          </button>
          {!entity.is_default && (
            <button
              onClick={onDelete}
              disabled={saving}
              className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-200 dark:hover:border-red-800 disabled:opacity-50"
              title="Delete entity"
            >
              <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
            </button>
          )}
        </div>
      </div>

      {xeroApplicable && (
      <div className="mt-4 flex flex-col gap-3">
        <div className="flex items-center gap-2 text-sm">
          {entity.xero_tenant_id ? (
            <>
              <Link2 className="w-4 h-4 text-emerald-500" />
              <span className="text-slate-700 dark:text-slate-300">
                Linked to {entity.xero_tenant_name || 'Xero tenant'}
              </span>
            </>
          ) : (
            <>
              <Unlink className="w-4 h-4 text-slate-400" />
              <span className="text-slate-500">Not linked to Xero</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!entity.xero_tenant_id && (
            <>
              <select
                value={selectedTenantId}
                onChange={(e) => onSelectTenant(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              >
                <option value="">Select Xero tenant...</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id} disabled={tenant.is_linked}>
                    {tenant.name}{tenant.is_linked ? ' (linked)' : ''}
                  </option>
                ))}
              </select>
              <button
                onClick={onLink}
                disabled={!selectedTenantId || saving}
                className="px-3 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
              >
                Link
              </button>
            </>
          )}
          {entity.xero_tenant_id && (
            <button
              onClick={onUnlink}
              disabled={saving}
              className="px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-700 text-amber-600 dark:text-amber-300 text-sm hover:bg-amber-50 dark:hover:bg-amber-900/20"
            >
              Unlink
            </button>
          )}
        </div>
      </div>
      )}
    </div>
  )
}
